import React, { useState, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { API_BASE_URL, RECAPTCHA_SITE_KEY } from '../../config';
import ReCAPTCHA from 'react-google-recaptcha';
import './Login.css';

interface LoginProps {
  onLogin: (username: string, password: string) => void;
  onRegister: (userData: any) => void;
  loading: boolean;
  initialView?: string;
  resetToken?: string | null;
}

const Login: React.FC<LoginProps> = ({ onLogin, onRegister, loading, initialView = 'landing', resetToken }) => {
  const { t } = useLanguage();
  const [formView, setFormView] = useState(initialView);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    first_name: '',
    last_name: '',
    motivation: '',
    new_password: '',
    repeat_password: '',
    token: resetToken || '',
    captcha_response: '',
    terms_accepted: false
  });
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [resetTokenState, setResetTokenState] = useState(resetToken || '');
  const [resetEmail, setResetEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});
  const [showSubmitErrors, setShowSubmitErrors] = useState(false);
  const firstErrorRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token && formView === 'reset-password') {
      setResetTokenState(token);
      setFormData(prev => ({ ...prev, token }));
    }
  }, [formView]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setShowSubmitErrors(false);

    if (formView === 'login') {
      onLogin(formData.username, formData.password);
    } else if (formView === 'register') {
      const errors: string[] = [];
      const fieldsToValidate = ['first_name', 'last_name', 'email', 'username', 'password'];
      fieldsToValidate.forEach(field => {
        const error = validateField(field, formData[field as keyof typeof formData] as string);
        if (error) {
          setFieldErrors(prev => ({ ...prev, [field]: error }));
          setTouchedFields(prev => ({ ...prev, [field]: true }));
          errors.push(error);
        }
      });
      if (!formData.terms_accepted) {
        errors.push('You must accept the Terms of Service to register');
      }
      if (!formData.captcha_response) {
        errors.push('Please complete the captcha verification');
      }
      if (errors.length > 0) {
        setShowSubmitErrors(true);
        setError(errors.join(', '));
        setTimeout(scrollToFirstError, 100);
        return;
      }
      onRegister(formData);
    } else if (formView === 'forgot-password') {
      try {
        const response = await fetch(`${API_BASE_URL}/api/forgot-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email })
        });
        const data = await response.json();
        if (response.ok) {
          if (data.google_account) {
            setError(t('auth.googleAccountHint') || 'This account uses Google Sign-In. Please log in with Google instead.');
          } else {
            setMessage(data.message);
            setFormData(prev => ({ ...prev, email: '' }));
          }
        } else {
          setError(data.error || 'Failed to send reset email');
        }
      } catch (error) {
        setError('Network error. Please try again.');
      }
    } else if (formView === 'reset-password') {
      try {
        const response = await fetch(`${API_BASE_URL}/api/reset-password`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            token: formData.token,
            new_password: formData.new_password,
            repeat_password: formData.repeat_password
          })
        });
        const data = await response.json();
        if (response.ok) {
          setMessage(data.message);
          setTimeout(() => {
            setFormView('login');
            setMessage('');
            setFormData(prev => ({ ...prev, new_password: '', repeat_password: '' }));
          }, 3000);
        } else {
          setError(data.error || 'Failed to reset password');
        }
      } catch (error) {
        setError('Network error. Please try again.');
      }
    }
  };

  const validateField = (fieldName: string, value: string) => {
    let error = '';
    switch (fieldName) {
      case 'email':
        if (value && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(value)) {
          error = t('auth.invalidEmail');
        }
        break;
      case 'password':
        if (value && value.length < 8) {
          error = t('auth.passwordMinLength');
        }
        break;
      case 'first_name':
        if (value && value.length < 2) {
          error = t('auth.firstNameMinLength');
        } else if (value && !/^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]+$/.test(value)) {
          error = t('auth.invalidFirstName');
        }
        break;
      case 'last_name':
        if (value && value.length < 2) {
          error = t('auth.lastNameMinLength');
        } else if (value && !/^[a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ\s-]+$/.test(value)) {
          error = t('auth.invalidLastName');
        }
        break;
      case 'username':
        if (value && (value.length < 3 || !/^[a-zA-Z0-9_-]+$/.test(value))) {
          error = t('auth.invalidUsername');
        }
        break;
    }
    return error;
  };

  const handleFieldBlur = (fieldName: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
    const error = validateField(fieldName, formData[fieldName as keyof typeof formData] as string);
    setFieldErrors(prev => ({ ...prev, [fieldName]: error }));
  };

  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCaptchaChange = (token: string | null) => {
    setFormData(prev => ({ ...prev, captcha_response: token || '' }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.checked }));
  };

  const scrollToFirstError = () => {
    if (firstErrorRef.current) {
      firstErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const googleSvg = (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path fill="#4285f4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34a853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#fbbc05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#ea4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
  );

  const renderLandingView = () => (
    <div className="login-card">
      <h1 className="login-landing__title">Train. Ride. Remember.</h1>
      <p className="login-landing__subtitle">Your epic cycling adventures, organized.</p>
      <p className="login-landing__text">
        <strong>AlpsEpics</strong> is more than just a club. It's a community of <strong>passionate cyclists</strong> who see the mountains as a challenge, and their bike as the way to conquer it. Together, we cross borders, conquer <strong>legendary passes</strong>, and forge <strong>memories that last a lifetime</strong>.
      </p>
      <div className="login-landing__actions">
        <button onClick={() => setFormView('login')} className="login-btn login-btn--landing">
          Log in
        </button>
        <button onClick={() => setFormView('register')} className="login-btn login-btn--secondary login-btn--landing">
          Join us
        </button>
      </div>
    </div>
  );

  const renderForgotPassword = () => (
    <div className="login-card">
      <h2 className="login-form__title">{t('auth.resetPassword')}</h2>
      <p className="login-form__subtitle">
        Enter your email address and we'll send you a link to reset your password.
      </p>
      <form onSubmit={handleSubmit}>
        <div className="login-field">
          <input
            type="email"
            name="email"
            placeholder={t('auth.email')}
            value={formData.email}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('email')}
            className={`login-input ${touchedFields.email && fieldErrors.email ? 'login-input--error' : ''}`}
            required
          />
          {touchedFields.email && fieldErrors.email && (
            <div ref={firstErrorRef} className="login-field-error">{fieldErrors.email}</div>
          )}
        </div>
        <button type="submit" disabled={loading} className="login-btn">
          {loading ? 'Sending...' : t('auth.sendResetLink')}
        </button>
      </form>
      <div className="login-form__links">
        <button onClick={() => setFormView('login')} className="login-link">
          {t('auth.backToLogin')}
        </button>
        <button onClick={() => setFormView('landing')} className="login-link login-link--muted">
          Back to main page
        </button>
      </div>
    </div>
  );

  const renderResetPassword = () => (
    <div className="login-card">
      <h2 className="login-form__title">{t('auth.resetPassword')}</h2>
      <p className="login-form__subtitle">Enter your new password below.</p>
      <form onSubmit={handleSubmit}>
        <div className="login-field">
          <input
            type="password"
            name="new_password"
            placeholder={t('auth.newPassword')}
            value={formData.new_password}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('new_password')}
            className="login-input"
            autoCapitalize="none"
            required
          />
        </div>
        <div className="login-field">
          <input
            type="password"
            name="repeat_password"
            placeholder={t('auth.repeatPassword')}
            value={formData.repeat_password}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('repeat_password')}
            className="login-input"
            autoCapitalize="none"
            required
          />
        </div>
        <button type="submit" disabled={loading} className="login-btn">
          {loading ? 'Resetting...' : t('auth.resetPassword')}
        </button>
      </form>
      <div className="login-form__links">
        <button onClick={() => setFormView('login')} className="login-link">
          {t('auth.backToLogin')}
        </button>
      </div>
    </div>
  );

  const renderLoginForm = () => (
    <div className="login-card">
      <h2 className="login-form__title">Log in</h2>
      <p className="login-form__subtitle">Log in to the AlpsEpics community</p>

      <form onSubmit={handleSubmit}>
        <div className="login-field">
          <input
            type="text"
            name="username"
            placeholder="Username"
            value={formData.username}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('username')}
            className={`login-input ${touchedFields.username && fieldErrors.username ? 'login-input--error' : ''}`}
            required
          />
          {touchedFields.username && fieldErrors.username && (
            <div className="login-field-error">{fieldErrors.username}</div>
          )}
        </div>

        <div className="login-field">
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('password')}
            className={`login-input ${touchedFields.password && fieldErrors.password ? 'login-input--error' : ''}`}
            autoCapitalize="none"
            required
          />
          {touchedFields.password && fieldErrors.password && (
            <div className="login-field-error">{fieldErrors.password}</div>
          )}
        </div>

        <button type="submit" disabled={loading} className="login-btn">
          {loading ? 'Processing...' : 'Log in'}
        </button>
      </form>

      <div className="login-divider">or</div>

      <button
        type="button"
        onClick={() => window.location.href = `${API_BASE_URL}/auth/google`}
        className="login-google-btn"
      >
        {googleSvg}
        Continue with Google
      </button>

      <div className="login-form__links">
        <button onClick={() => setFormView('forgot-password')} className="login-link">
          {t('auth.forgotPassword')}
        </button>
        <button onClick={() => setFormView('register')} className="login-link">
          Don't have an account? Register
        </button>
        <button onClick={() => setFormView('landing')} className="login-link login-link--muted">
          Back to main page
        </button>
      </div>
    </div>
  );

  const renderRegisterForm = () => (
    <div className="login-card">
      <h2 className="login-form__title">Register</h2>
      <p className="login-form__subtitle">Create an account in the AlpsEpics community</p>

      <button
        type="button"
        onClick={() => window.location.href = `${API_BASE_URL}/auth/google`}
        className="login-google-btn"
      >
        {googleSvg}
        Continue with Google
      </button>

      <div className="login-divider">or</div>

      {showSubmitErrors && error && (
        <div className="login-error-banner">
          <strong>Please fix the following errors:</strong>
          <ul>
            {error.split(', ').map((err, index) => (
              <li key={index}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="login-field">
          <input
            type="text"
            name="first_name"
            placeholder="First name"
            value={formData.first_name}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('first_name')}
            className={`login-input ${touchedFields.first_name && fieldErrors.first_name ? 'login-input--error' : ''}`}
            required
          />
          {touchedFields.first_name && fieldErrors.first_name && (
            <div ref={firstErrorRef} className="login-field-error">{fieldErrors.first_name}</div>
          )}
        </div>

        <div className="login-field">
          <input
            type="text"
            name="last_name"
            placeholder="Last name"
            value={formData.last_name}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('last_name')}
            className={`login-input ${touchedFields.last_name && fieldErrors.last_name ? 'login-input--error' : ''}`}
            required
          />
          {touchedFields.last_name && fieldErrors.last_name && (
            <div className="login-field-error">{fieldErrors.last_name}</div>
          )}
        </div>

        <div className="login-field">
          <input
            type="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('email')}
            className={`login-input ${touchedFields.email && fieldErrors.email ? 'login-input--error' : ''}`}
            required
          />
          {touchedFields.email && fieldErrors.email && (
            <div className="login-field-error">{fieldErrors.email}</div>
          )}
        </div>

        <div className="login-field">
          <input
            type="text"
            name="username"
            placeholder="Username"
            value={formData.username}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('username')}
            className={`login-input ${touchedFields.username && fieldErrors.username ? 'login-input--error' : ''}`}
            required
          />
          {touchedFields.username && fieldErrors.username && (
            <div className="login-field-error">{fieldErrors.username}</div>
          )}
        </div>

        <div className="login-field">
          <input
            type="password"
            name="password"
            placeholder="Password"
            value={formData.password}
            onChange={handleFieldChange}
            onBlur={() => handleFieldBlur('password')}
            className={`login-input ${touchedFields.password && fieldErrors.password ? 'login-input--error' : ''}`}
            autoCapitalize="none"
            required
          />
          {touchedFields.password && fieldErrors.password && (
            <div className="login-field-error">{fieldErrors.password}</div>
          )}
        </div>

        <div className="login-field">
          <textarea
            name="motivation"
            placeholder="Why do you want to join the club? (optional)"
            value={formData.motivation}
            onChange={handleFieldChange}
            rows={3}
            className="login-textarea"
          />
        </div>

        <div className="login-captcha">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={handleCaptchaChange}
            theme="light"
          />
        </div>

        <label className="login-terms">
          <input
            type="checkbox"
            name="terms_accepted"
            checked={formData.terms_accepted}
            onChange={handleCheckboxChange}
            required
          />
          <span>
            I accept the Terms of Service (
            <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer">
              View Terms
            </a>
            )
          </span>
        </label>

        <button type="submit" disabled={loading} className="login-btn">
          {loading ? 'Processing...' : 'Register'}
        </button>
      </form>

      <div className="login-form__links">
        <button onClick={() => setFormView('login')} className="login-link">
          Already have an account? Log in
        </button>
        <button onClick={() => setFormView('landing')} className="login-link login-link--muted">
          Back to main page
        </button>
      </div>
    </div>
  );

  const renderFormContent = () => {
    switch (formView) {
      case 'landing': return renderLandingView();
      case 'login': return renderLoginForm();
      case 'register': return renderRegisterForm();
      case 'forgot-password': return renderForgotPassword();
      case 'reset-password': return renderResetPassword();
      default: return renderLandingView();
    }
  };

  return (
    <div className="login-shell">
      <div className="login-hero">
        <img
          src={`${process.env.PUBLIC_URL}/assets/tlo_main.jpg`}
          alt=""
          className="login-hero__bg"
        />
        <div className="login-hero__overlay" />
        <div className="login-hero__content">
          <img
            src={`${process.env.PUBLIC_URL}/alpsepics-logo.png`}
            alt="AlpsEpics"
            className="login-hero__logo"
          />
          <h1 className="login-hero__brand">AlpsEpics</h1>
          <p className="login-hero__tagline">Train. Ride. Remember.</p>
        </div>
      </div>

      <div className="login-panel">
        <div className="login-panel__inner">
          {renderFormContent()}

          {message && (
            <div className="login-message login-message--success">{message}</div>
          )}
          {error && !showSubmitErrors && (
            <div className="login-message login-message--error">{error}</div>
          )}

          <div className="login-footer">
            &copy; {new Date().getFullYear()} AlpsEpics. All rights reserved.
            <br />
            Crafted &amp; Designed &amp; Built with <span style={{ color: '#C43838' }}>&hearts;</span> by <a href="https://codezuno.com" target="_blank" rel="noopener noreferrer">codezuno.com</a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
